import React, { useState, useMemo } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { FiCode, FiEye } from 'react-icons/fi';

const RichTextEditor = ({ value, onChange, placeholder = 'Enter text...', height = '300px',label = 'Content' }) => {
  const [showSource, setShowSource] = useState(false);
  const [sourceCode, setSourceCode] = useState(value || '');

  // Quill modules configuration
  const modules = useMemo(() => ({
    toolbar: [
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      [{ 'font': [] }],
      [{ 'size': ['small', false, 'large', 'huge'] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'script': 'sub' }, { 'script': 'super' }],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      [{ 'indent': '-1' }, { 'indent': '+1' }],
      [{ 'align': [] }],
      ['blockquote', 'code-block'],
      ['link', 'image', 'video'],
      ['clean']
    ],
    clipboard: {
      matchVisual: false
    }
  }), []);

  // Quill formats
  const formats = [
    'header', 'font', 'size',
    'bold', 'italic', 'underline', 'strike',
    'color', 'background',
    'script',
    'list', 'bullet', 'indent',
    'align',
    'blockquote', 'code-block',
    'link', 'image', 'video'
  ];

  const handleEditorChange = (content) => {
    setSourceCode(content);
    onChange(content);
  };

  const handleSourceChange = (e) => {
    const newContent = e.target.value;
    setSourceCode(newContent);
    onChange(newContent);
  };

  const toggleSourceView = () => {
    setShowSource(!showSource);
  };

  return (
    <div className="rich-text-editor-wrapper">
      {/* Toolbar with Source Code Toggle */}
  <div className="flex justify-between items-center mb-2">
  <h3 className="block text-sm font-medium text-gray-700">
    {label}
  </h3>
  <button
    type="button"
    onClick={toggleSourceView}
    className="flex items-center space-x-2 px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
  >
    {showSource ? (
      <>
        <FiEye className="w-4 h-4" />
        <span>Visual Editor</span>
      </>
    ) : (
      <>
        <FiCode className="w-4 h-4" />
        <span>Source Code</span>
      </>
    )}
  </button>
</div>

      {/* Editor or Source Code View */}
      {showSource ? (
        <div className="border border-gray-300 rounded-md">
          <textarea
            value={sourceCode}
            onChange={handleSourceChange}
            className="w-full p-4 font-mono text-sm border-0 focus:ring-2 focus:ring-blue-500 rounded-md"
            style={{ height, resize: 'vertical' }}
            placeholder="HTML source code..."
          />
        </div>
      ) : (
        <div className="border border-gray-300 rounded-md overflow-hidden">
          <ReactQuill
            theme="snow"
            value={value}
            onChange={handleEditorChange}
            modules={modules}
            formats={formats}
            placeholder={placeholder}
            style={{ height }}
          />
        </div>
      )}

      {/* Custom Styles */}
      <style jsx>{`
        .rich-text-editor-wrapper :global(.ql-container) {
          font-size: 14px;
          font-family: inherit;
        }
        
        .rich-text-editor-wrapper :global(.ql-editor) {
          min-height: ${height};
          max-height: 600px;
          overflow-y: auto;
        }
        
        .rich-text-editor-wrapper :global(.ql-toolbar) {
          background-color: #f9fafb;
          border-top-left-radius: 0.375rem;
          border-top-right-radius: 0.375rem;
        }
        
        .rich-text-editor-wrapper :global(.ql-container) {
          border-bottom-left-radius: 0.375rem;
          border-bottom-right-radius: 0.375rem;
        }
        
        .rich-text-editor-wrapper :global(.ql-editor.ql-blank::before) {
          color: #9ca3af;
          font-style: normal;
        }
      `}</style>
    </div>
  );
};

export default RichTextEditor;


